import { FileRepository } from "@/components/features";
import { Form } from "@/components/ui";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { FormSubmitButton } from "@/components/ui/FormFields/FormSubmitButton/FormSubmitButton";
import { SheetFooter } from "@/components/ui/shadcn/Sheet/Sheet";
import { Slideover } from "@/components/ui/shadcn/Slideover/Slideover";
import type { Matter, MatterFile } from "@/lib/types/Matter";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const fileRepoFormSchema = z.object({
  selectedFiles: z.array(z.any()).optional(),
});
const defaultFileRepoFormValues = {
  selectedFiles: [],
};

export const SelectFileRepoPhotosSlideover = ({
  matter,
  open,
  setOpen,
  onSaveSelectedFiles,
}: {
  matter?: Matter;
  open: boolean;
  setOpen: (open: boolean) => void;
  onSaveSelectedFiles: (files: MatterFile[]) => void;
}) => {
  const fileRepoForm = useForm<z.infer<typeof fileRepoFormSchema>>({
    resolver: zodResolver(fileRepoFormSchema),
    defaultValues: defaultFileRepoFormValues,
    mode: "onSubmit",
  });

  const [selectedFiles, setSelectedFiles] = useState<MatterFile[]>([]);
  const updateSelectedFiles = useCallback(
    (nextSelectedFiles: MatterFile[]) => {
      const currentIds = new Set(selectedFiles.map((file) => file.id));
      const nextIds = new Set(nextSelectedFiles.map((file) => file.id));
      const isSameSelection =
        currentIds.size === nextIds.size &&
        Array.from(nextIds).every((id) => currentIds.has(id));

      if (isSameSelection) return;

      setSelectedFiles(nextSelectedFiles);
      fileRepoForm.setValue("selectedFiles", nextSelectedFiles, {
        shouldDirty: true,
      });
    },
    [fileRepoForm, selectedFiles],
  );

  const handleExternalSelectionChange = useCallback(
    (fileIds: string[], checked: boolean, replaceSelection = false) => {
      if (!matter) return;
      const nextSelectedFiles = replaceSelection
        ? matter.files.filter((file) => fileIds.includes(file.id))
        : checked
          ? [
              ...selectedFiles,
              ...fileIds
                .filter(
                  (fileId) => !selectedFiles.some((file) => file.id === fileId),
                )
                .map((fileId) =>
                  matter.files.find((file) => file.id === fileId),
                )
                .filter(Boolean),
            ]
          : selectedFiles.filter((file) => !fileIds.includes(file.id));

      updateSelectedFiles(nextSelectedFiles as MatterFile[]);
    },
    [matter, selectedFiles, updateSelectedFiles],
  );

  useEffect(() => {
    if (!open) {
      setSelectedFiles(defaultFileRepoFormValues.selectedFiles);
      fileRepoForm.setValue(
        "selectedFiles",
        defaultFileRepoFormValues.selectedFiles,
        {
          shouldDirty: true,
        },
      );
    }
  }, [open, fileRepoForm]);

  return (
    <Slideover
      title="Select file"
      open={open}
      setOpen={setOpen}
      size="large"
      isDirty={selectedFiles.length > 0}
    >
      <Form {...fileRepoForm}>
        <form
          className="flex h-full flex-col overflow-hidden"
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await fileRepoForm.handleSubmit(() => {
              onSaveSelectedFiles(selectedFiles);
            })();
          }}
        >
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="flex min-h-0 flex-1 overflow-hidden">
              {matter && (
                <FileRepository
                  matter={matter}
                  useExternalSelection
                  onExternalSelectionChange={handleExternalSelectionChange}
                  selectedFiles={selectedFiles}
                  enabledFileTypes={["pdf", "jpg", "png", "jpeg"]}
                  showMergeDocuments={false}
                  showShareFolders={false}
                  showSelectionActions={false}
                  enableTreeDrag={false}
                  compactPreviewLayout
                />
              )}
            </div>
          </div>
          <SheetFooter addSlideoverStyles>
            <FormCancelButton
              setOpen={setOpen}
              isDirty={selectedFiles.length > 0}
            />
            <FormSubmitButton loading={false} />
          </SheetFooter>
        </form>
      </Form>
    </Slideover>
  );
};
